import React, { useCallback, useEffect, useState } from 'react'
import {
  AppState,
  Linking,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  TouchableOpacity,
  View,
} from 'react-native'
import { useFocusEffect, useNavigation, useTheme } from '@react-navigation/native'
import type { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { Screen, Header, Text, Icon } from '@components'
import type { MenuStackParamList } from '../../../navigation/navigator/MenuNavigator'
import { useSettingsNavigation } from './hook'
import { getSettingsStyles } from './style'
import {
  useCreationStackIntegration,
  useScreen,
  useUpdateProfile,
  usePremium,
  useExportContacts,
  useExportEventsData,
  useDeleteAccount,
  useGetNearbyVisibility,
  useAccountSecurity,
} from '@hooks'
import { resolveLanguage, useLanguage, useAuth } from '@context'
import { useLanguageStore, useCalendarSyncStore } from '@store'
import type { Language } from '@context'
import { getCurrentPosition, getLocationPermissionStatus, showErrorToast } from '@utils'
import type { LocationPermissionStatus } from '@utils'
import { ensurePermission } from '../../../utils/calendarSync/nativeCalendar'
import { DeleteAccountModal } from './components/DeleteAccountModal'
import { CreatePasswordModal } from './components/CreatePasswordModal'
import { VisibilityPanel } from '../nearby/components/VisibilityPanel'
import { getNearbyVisibilityState } from '../nearby/nearbyVisibility'

type LanguageOption = {
  value: Language
  labelKey: string
}

const languageOptions: LanguageOption[] = [
  { value: 'auto', labelKey: 'settings.auto' },
  { value: 'fr', labelKey: 'settings.french' },
  { value: 'en', labelKey: 'settings.english' },
  { value: 'es', labelKey: 'settings.spanish' },
]

export const Settings: React.FC = () => {
  const theme = useTheme()
  const styles = getSettingsStyles(theme)
  const { navigation } = useSettingsNavigation()
  const menuNavigation = useNavigation<NativeStackNavigationProp<MenuStackParamList>>()
  const { isWeb } = useScreen()
  const { t } = useLanguage()
  const { language, switchLanguage } = useLanguageStore()
  const { mutate: updateProfile } = useUpdateProfile()
  // Null until the device has answered, so that no status flashes.
  const [locationPermission, setLocationPermission] = useState<LocationPermissionStatus | null>(
    null,
  )
  const {
    data: visibilityData,
    isLoading: isVisibilityLoading,
    isError: isVisibilityError,
  } = useGetNearbyVisibility()
  const [showVisibilityPanel, setShowVisibilityPanel] = useState(false)
  const calendarSyncEnabled = useCalendarSyncStore((s) => s.enabled)
  const setCalendarSyncEnabled = useCalendarSyncStore((s) => s.setEnabled)
  const { isPremium } = usePremium()
  const { exportContacts, isExporting } = useExportContacts()
  const { exportEventsData, exportingFormat } = useExportEventsData()
  const { signOut } = useAuth()
  const { mutateAsync: deleteAccount, isPending: isDeletingAccount } = useDeleteAccount()
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false)
  const { showNotificationSettings, isCreationStackEnabled } = useCreationStackIntegration()
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // Only accounts created through an event code have no password yet.
  const { data: accountSecurity } = useAccountSecurity()
  const [isPasswordModalVisible, setPasswordModalVisible] = useState(false)

  const openDeleteModal = (): void => {
    setDeleteError(null)
    setDeleteModalVisible(true)
  }

  const closeDeleteModal = (): void => {
    setDeleteModalVisible(false)
    setDeleteError(null)
  }

  const handleConfirmDelete = async (password: string): Promise<void> => {
    setDeleteError(null)
    try {
      await deleteAccount(password)
      setDeleteModalVisible(false)
      signOut()
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      setDeleteError(
        status === 401
          ? t('settings.deleteAccountWrongPassword')
          : status === 409
            ? t('settings.deleteAccountNeedPassword')
            : t('settings.deleteAccountError'),
      )
    }
  }

  const onToggleCalendarSync = async (value: boolean): Promise<void> => {
    if (value && Platform.OS !== 'web') {
      const granted = await ensurePermission()
      if (!granted) {
        showErrorToast(t('settings.calendarSyncPermissionDenied'))
        return
      }
    }
    setCalendarSyncEnabled(value)
  }

  const handleLanguageChange = (newLanguage: Language) => {
    switchLanguage(newLanguage)
    // Persist to backend. « Automatique » saves the device's language, read
    // the way the app does at start-up: the `require` that stood here does not
    // exist on the website, where French was saved whatever the browser.
    updateProfile({ language: resolveLanguage(newLanguage), _silent: true })
  }

  const refreshLocationPermission = useCallback(async () => {
    setLocationPermission(await getLocationPermissionStatus())
  }, [])

  // The permission belongs to the device and can change outside the app: it
  // is read each time the page opens, and back from the system settings.
  useFocusEffect(
    useCallback(() => {
      refreshLocationPermission()
    }, [refreshLocationPermission]),
  )
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') refreshLocationPermission()
    })
    return () => subscription.remove()
  }, [refreshLocationPermission])

  const handleAllowLocation = async () => {
    // Asking for the position is what makes the device show its prompt.
    const coords = await getCurrentPosition()
    // A position that was read proves the permission, even where the browser
    // cannot say so (no Permissions API, or allowed for this time only).
    if (coords) {
      setLocationPermission('granted')
      return
    }
    const status = await getLocationPermissionStatus()
    setLocationPermission(status)
    // The phone apps have no browser and no site: they point to the phone settings.
    if (status !== 'granted') {
      showErrorToast(t(isWeb ? 'settings.locationDenied' : 'settings.locationDeniedApp'))
    }
  }

  const locationPermissionKeys: Record<LocationPermissionStatus, string> = {
    granted: 'settings.locationPermissionGranted',
    denied: 'settings.locationPermissionDenied',
    undetermined: 'settings.locationPermissionUndetermined',
    unknown: 'settings.locationPermissionUnknown',
  }
  const canAskLocation = locationPermission === 'undetermined' || locationPermission === 'unknown'

  const visibilityKeys: Record<string, string> = {
    ALL: 'nearby.visibilityAllMobile',
    PROFESSIONAL: 'nearby.visibilityProfessionalMobile',
    PRIVATE: 'nearby.visibilityPrivateMobile',
    HIDDEN: 'nearby.visibilityHiddenMobile',
    LOADING: 'nearby.visibilityLoading',
    UNKNOWN: 'nearby.visibilityUnknown',
  }
  const visibilityKey =
    visibilityKeys[
      getNearbyVisibilityState(isVisibilityLoading, isVisibilityError, visibilityData?.scope)
    ]

  const handleOpenSystemSettings = () => {
    if (Platform.OS !== 'web' && typeof Linking.openSettings === 'function') {
      Linking.openSettings()
    }
  }

  return (
    <Screen>
      <Header
        title={t('settings.title')}
        iconLeft={isWeb ? undefined : 'arrowBack'}
        onPressLeft={() => navigation.goBack()}
      />

      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Language */}
        <Text type="bodyBold" style={styles.sectionTitle}>
          {t('settings.language')}
        </Text>
        <Text type="bodySmall" style={styles.sectionDescription}>
          {t('settings.languageDescription')}
        </Text>

        <View style={styles.optionsContainer}>
          {languageOptions.map((option) => {
            const isSelected = language === option.value
            return (
              <TouchableOpacity
                key={option.value}
                style={[styles.optionCard, isSelected && styles.optionCardSelected]}
                onPress={() => handleLanguageChange(option.value)}
                activeOpacity={0.7}
              >
                <Text
                  type="body"
                  style={StyleSheet.flatten([
                    styles.optionText,
                    isSelected && styles.optionTextSelected,
                  ])}
                >
                  {t(option.labelKey)}
                </Text>
                {isSelected && <Icon name="check" size={20} color={theme.colors.primary} />}
              </TouchableOpacity>
            )
          })}
        </View>

        {/* Location / geolocation */}
        <View style={{ marginTop: theme.spacing.xxl }}>
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('settings.location')}
          </Text>
          <Text type="bodySmall" style={styles.sectionDescription}>
            {t('settings.locationDescription')}
          </Text>
          <View style={styles.optionCard}>
            <View style={styles.locationLabel}>
              <Icon name="mapPin" size={20} color={theme.colors.primary} />
              <View style={styles.locationTextBlock}>
                <Text type="body" style={styles.optionText}>
                  {t('settings.locationPermission')}
                </Text>
                <Text type="bodySmall" style={styles.locationValue} testID="settings-location-permission">
                  {locationPermission ? t(locationPermissionKeys[locationPermission]) : ''}
                </Text>
                {isWeb && locationPermission === 'denied' && (
                  <Text type="bodySmall" style={styles.locationValue}>
                    {t('settings.locationDenied')}
                  </Text>
                )}
              </View>
            </View>
            {canAskLocation && (
              <TouchableOpacity
                testID="settings-location-allow"
                activeOpacity={0.7}
                onPress={handleAllowLocation}
              >
                <Text type="bodyBold" color={theme.colors.primary}>
                  {t('settings.locationAllow')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
          {!isWeb && (
            <TouchableOpacity
              style={[styles.optionCard, { marginTop: theme.spacing.sm }]}
              onPress={handleOpenSystemSettings}
              activeOpacity={0.7}
            >
              <Text type="body" style={styles.optionText}>
                {t('settings.openPhoneSettings')}
              </Text>
              <Icon name="settings" size={20} color={theme.colors.primary} />
            </TouchableOpacity>
          )}
          {/* The real on/off: someone invisible has no position saved or shown. */}
          <View style={[styles.optionCard, { marginTop: theme.spacing.sm }]}>
            <View style={styles.locationLabel}>
              <Icon name="eye" size={20} color={theme.colors.primary} />
              <View style={styles.locationTextBlock}>
                <Text type="body" style={styles.optionText}>
                  {t('settings.locationVisibility')}
                </Text>
                <Text type="bodySmall" style={styles.locationValue} testID="settings-location-visibility-value">
                  {visibilityKey ? t(visibilityKey) : ''}
                </Text>
              </View>
            </View>
            <TouchableOpacity
              testID="settings-location-visibility"
              activeOpacity={0.7}
              onPress={() => setShowVisibilityPanel(true)}
            >
              <Text type="bodyBold" color={theme.colors.primary}>
                {t('common.edit')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Contacts export (Premium) */}
        <View style={{ marginTop: theme.spacing.xxl }}>
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('export.sectionTitle')}
          </Text>
          <Text type="bodySmall" style={styles.sectionDescription}>
            {t('export.sectionDescription')}
          </Text>
          <TouchableOpacity
            testID="settings-export-contacts"
            style={styles.optionCard}
            onPress={exportContacts}
            disabled={isExporting}
            activeOpacity={0.7}
          >
            <View style={styles.locationLabel}>
              <Icon name="save" size={20} color={theme.colors.primary} />
              <Text type="body" style={styles.optionText}>
                {isExporting ? t('export.exporting') : t('export.cta')}
              </Text>
            </View>
            {!isPremium && <Icon name="star" size={18} color={theme.colors.gold} />}
          </TouchableOpacity>
        </View>

        {/* Events data export (free for everyone) */}
        <View style={{ marginTop: theme.spacing.xxl }}>
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('eventsExport.sectionTitle')}
          </Text>
          <Text type="bodySmall" style={styles.sectionDescription}>
            {t('eventsExport.sectionDescription')}
          </Text>
          <TouchableOpacity
            testID="settings-export-events-json"
            style={styles.optionCard}
            onPress={() => exportEventsData('json')}
            disabled={exportingFormat !== null}
            activeOpacity={0.7}
          >
            <View style={styles.locationLabel}>
              <Icon name="save" size={20} color={theme.colors.primary} />
              <Text type="body" style={styles.optionText}>
                {exportingFormat === 'json' ? t('eventsExport.exporting') : t('eventsExport.ctaJson')}
              </Text>
            </View>
          </TouchableOpacity>
          <TouchableOpacity
            testID="settings-export-events-csv"
            style={[styles.optionCard, { marginTop: theme.spacing.sm }]}
            onPress={() => exportEventsData('csv')}
            disabled={exportingFormat !== null}
            activeOpacity={0.7}
          >
            <View style={styles.locationLabel}>
              <Icon name="save" size={20} color={theme.colors.primary} />
              <Text type="body" style={styles.optionText}>
                {exportingFormat === 'csv' ? t('eventsExport.exporting') : t('eventsExport.ctaCsv')}
              </Text>
            </View>
          </TouchableOpacity>
        </View>

        {/* Calendar sync (native only) */}
        {Platform.OS !== 'web' && (
          <View style={{ marginTop: theme.spacing.xxl }}>
            <Text type="bodyBold" style={styles.sectionTitle}>
              {t('settings.calendarSync')}
            </Text>
            <Text type="bodySmall" style={styles.sectionDescription}>
              {t('settings.calendarSyncDescription')}
            </Text>
            <View style={styles.optionCard}>
              <Text type="body" style={styles.optionText}>
                {t('settings.calendarSyncEnable')}
              </Text>
              <Switch
                testID="settings-calendar-sync-switch"
                value={calendarSyncEnabled}
                onValueChange={onToggleCalendarSync}
              />
            </View>
          </View>
        )}

        {/* Notifications: when the phone rings */}
        <View style={{ marginTop: theme.spacing.xxl }}>
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('settings.notifications')}
          </Text>
          <Text type="bodySmall" style={styles.sectionDescription}>
            {t('settings.notificationsDescription')}
          </Text>
          <TouchableOpacity
            testID="settings-notifications-row"
            style={styles.optionCard}
            activeOpacity={0.7}
            onPress={() =>
              isCreationStackEnabled
                ? showNotificationSettings()
                : menuNavigation.navigate('NotificationSettings')
            }
          >
            <Text type="body" style={styles.optionText}>
              {t('notificationSettings.title')}
            </Text>
            <Icon name="arrowRight" size={20} color={theme.colors.primary} />
          </TouchableOpacity>
        </View>

        {/* Subscription status */}
        <View style={{ marginTop: theme.spacing.xxl }}>
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('settings.subscription')}
          </Text>
          <Text type="bodySmall" style={styles.sectionDescription}>
            {t('settings.subscriptionDescription')}
          </Text>
          <TouchableOpacity
            testID="settings-subscription-row"
            style={styles.optionCard}
            activeOpacity={0.7}
            onPress={() => menuNavigation.navigate('Subscription')}
          >
            <Text type="body" style={styles.optionText}>
              {isPremium ? t('settings.subscriptionPremium') : t('settings.subscriptionManage')}
            </Text>
            <Icon name="arrowRight" size={20} color={theme.colors.primary} />
          </TouchableOpacity>
        </View>

        {/* Legal pages: their only entry on phones and in the native apps */}
        <View style={{ marginTop: theme.spacing.xxl }}>
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('settings.legal')}
          </Text>
          <Text type="bodySmall" style={styles.sectionDescription}>
            {t('settings.legalDescription')}
          </Text>
          <TouchableOpacity
            testID="settings-legal-notice-row"
            style={styles.optionCard}
            activeOpacity={0.7}
            onPress={() => menuNavigation.navigate('LegalNotice')}
          >
            <Text type="body" style={styles.optionText}>
              {t('webFooter.legalNotice')}
            </Text>
            <Icon name="arrowRight" size={20} color={theme.colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity
            testID="settings-privacy-policy-row"
            style={[styles.optionCard, { marginTop: theme.spacing.sm }]}
            activeOpacity={0.7}
            onPress={() => menuNavigation.navigate('PrivacyPolicy')}
          >
            <Text type="body" style={styles.optionText}>
              {t('webFooter.privacyPolicy')}
            </Text>
            <Icon name="arrowRight" size={20} color={theme.colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity
            testID="settings-contact-us-row"
            style={[styles.optionCard, { marginTop: theme.spacing.sm }]}
            activeOpacity={0.7}
            onPress={() => menuNavigation.navigate('ContactUs')}
          >
            <Text type="body" style={styles.optionText}>
              {t('webFooter.contactUs')}
            </Text>
            <Icon name="arrowRight" size={20} color={theme.colors.primary} />
          </TouchableOpacity>
        </View>

        {/* Password — only for accounts that still have none */}
        {accountSecurity?.hasPassword === false && (
          <View style={{ marginTop: theme.spacing.xxl }}>
            <Text type="bodyBold" style={styles.sectionTitle}>
              {t('createPassword.settingsTitle')}
            </Text>
            <Text type="bodySmall" style={styles.sectionDescription}>
              {t('createPassword.settingsDescription')}
            </Text>
            <TouchableOpacity
              testID="settings-create-password-row"
              style={styles.optionCard}
              activeOpacity={0.7}
              onPress={() => setPasswordModalVisible(true)}
            >
              <Text type="body" style={styles.optionText}>
                {t('createPassword.settingsButton')}
              </Text>
              <Icon name="lock" size={20} color={theme.colors.primary} />
            </TouchableOpacity>
          </View>
        )}

        {/* Danger zone — account deletion (App Store guideline 5.1.1(v)) */}
        <View style={{ marginTop: theme.spacing.xxl }}>
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('settings.deleteAccountTitle')}
          </Text>
          <Text type="bodySmall" style={styles.sectionDescription}>
            {t('settings.deleteAccountDescription')}
          </Text>
          <TouchableOpacity
            testID="settings-delete-account-row"
            style={styles.optionCard}
            activeOpacity={0.7}
            onPress={openDeleteModal}
          >
            <Text type="body" style={[styles.optionText, { color: theme.colors.error }]}>
              {t('settings.deleteAccountButton')}
            </Text>
            <Icon name="trash" size={20} color={theme.colors.error} />
          </TouchableOpacity>
        </View>
      </ScrollView>

      <CreatePasswordModal
        isVisible={isPasswordModalVisible}
        onClose={() => setPasswordModalVisible(false)}
      />
      <DeleteAccountModal
        isVisible={isDeleteModalVisible}
        isDeleting={isDeletingAccount}
        errorMessage={deleteError}
        hasPassword={accountSecurity?.hasPassword !== false}
        onCreatePassword={() => {
          closeDeleteModal()
          setPasswordModalVisible(true)
        }}
        onCancel={closeDeleteModal}
        onConfirm={handleConfirmDelete}
      />

      {/* Same chooser as « À proximité »: a full sheet on phones, a card on web */}
      <Modal
        visible={showVisibilityPanel}
        animationType={isWeb ? 'fade' : 'slide'}
        transparent={isWeb}
        presentationStyle={isWeb ? undefined : 'pageSheet'}
        onRequestClose={() => setShowVisibilityPanel(false)}
      >
        {isWeb ? (
          <View style={styles.visibilityBackdrop}>
            {/* Scrolls, so the update button stays reachable in a low window. */}
            <ScrollView
              style={styles.visibilityCard}
              contentContainerStyle={styles.visibilityCardContent}
            >
              <VisibilityPanel onClose={() => setShowVisibilityPanel(false)} />
            </ScrollView>
          </View>
        ) : (
          <VisibilityPanel onClose={() => setShowVisibilityPanel(false)} />
        )}
      </Modal>
    </Screen>
  )
}
